import React, { Dispatch, SetStateAction, useCallback, useEffect, useReducer, useRef, useState } from "react";
import { LoadingOverlay, Loader } from "react-overlay-loader";
import MessageListItem from "./MessageListItem";
import MessageToolbar from "./MessageToolbar";
import {
    ArchiveConstants,
    DEFAULT_PAGE_SIZE,
    MessageListConstants,
    SortAndFilterConstants,
    SortAndFilterIconType,
} from "src/Core/Common/Constants";
import DefaultPagination from "src/components/Common/ui/DefaultPagination";
import { DeliveryType, FolderType } from "src/Core/Common/Enums";
import { IReceivedMessageModel } from "src/Core/ViewModels/Message/ReceivedMessageModel";
import { receivedMessageReducer } from "src/reducers/selectedMessageReducer";
import { IFolderModel, IPagination } from "src/Core/ViewModels/Folder/FolderModel";
import { AppNotifier } from "src/components/Common/Notification/AppNotifier";
import { useDispatch, useSelector } from "react-redux";
import { MovingMessageActions } from "src/reducers/movingMessageReducer";
import { ApplicationState } from "src/store";
import { getFolderMessageSortOptions, getMessageListWrapperHeight } from "src/helper/HelperFunctions";
import { IResourceLocator } from "src/helper/ResourceIdLocators";
import { Col, FormControl, Row } from "react-bootstrap";
import { RefreshIcon, SearchIcon } from "src/assets/images/svg/IconCollection";
import { debounce } from "throttle-debounce";
import { FiltersCustomComponent } from "src/components/Common/ui/Filters/FiltersCustomComponent";
import { ToasterMessages } from "src/helper/ToasterMessages";
import { ISentMessageModel } from "src/Core/ViewModels/Message/SentMessageModel";
import { getTotalMessageCount, areThreadsValid } from "src/helper/MessageHelperThreads";

const initialPagination = {
    pageSize: DEFAULT_PAGE_SIZE,
    currentPage: 1,
    totalRecords: 0,
};
interface IMessageListProps {
    messages: Array<IReceivedMessageModel>;
    totalRecords: number;
    folderId: number;
    folderType: FolderType;
    isLoading: boolean;
    resourceLocators: IResourceLocator;
    pagination: IPagination;
    clearCheckBoxSelection: boolean;
    folder?: IFolderModel;
    reload?: boolean;
    showMessageDetail: () => void;
    setPagination: Dispatch<SetStateAction<IPagination>>;
    deleteMessages?: (messages: IReceivedMessageModel[], callback: any) => void;
    getMessageDetail?: (message: IReceivedMessageModel) => void;
    fetchMessages: (
        pageNo: number,
        pageSize: number,
        sortColumn?: string,
        sortOrder?: string,
        search?: string,
        callback?: any
    ) => void;
    pageNumberChanged?: (pageNo: number) => void;
    markAsNotSpam?: (messageIds: number[], callback?: any) => void;
    moveToInbox?: (messageIds: number[], callback?: any) => void;
    print?: (messages: IReceivedMessageModel[]) => void;
    checkFolderExpiry?: (folderId: number) => boolean;
    moveToFolder?: (messages: IReceivedMessageModel[], folderId: number, callback?: any) => void;
    hideMessageDetailView(hide: boolean): void;
    setSelection: React.Dispatch<React.SetStateAction<boolean>>;
    handleFolderRefreshClick?: () => void;
    isFolderRefresh?: boolean;
    selectedMessage: ISentMessageModel | IReceivedMessageModel;
    setSelectedMessage: Dispatch<SetStateAction<ISentMessageModel | IReceivedMessageModel>>;
}

const sortingData = [
    {
        title: SortAndFilterConstants.SORT_BY_TEXT,
        menus: [
            SortAndFilterConstants.ALPHABETICAL_ORDER_ASC,
            SortAndFilterConstants.ALPHABETICAL_ORDER_DESC,
            SortAndFilterConstants.NEWEST_FIRST_ORDER,
            SortAndFilterConstants.OLDEST_FIRST_ORDER,
        ],
    },
];

const MessageList: React.FC<IMessageListProps> = props => {
    const [pagination, setPagination] = useState(initialPagination);
    const [searchText, setSearchText] = useState<string>("");
    const [selectedSortOption, setSelectedSortOption] = useState<string>(SortAndFilterConstants.NEWEST_FIRST_ORDER);
    const [selectedReceivedMessage, dispatchSelectedMessageActions] = useReducer(receivedMessageReducer, {
        messages: [],
        isAllSelected: false,
    });
    const { mailboxUserData } = useSelector((state: ApplicationState) => state.commonState);
    const dispatch = useDispatch();
    const [isMoveToFoldersAllowed, setIsMoveToFolders] = useState(false);
    const [collapseAllThreads, setCollapseThreads] = React.useState(false);

    const signalRState = useSelector((appState: ApplicationState) => appState.signalRState);
    useEffect(() => {
        const sort = getFolderMessageSortOptions(selectedSortOption);
        setPagination(initialPagination);
        props.fetchMessages &&
            props.fetchMessages(
                initialPagination.currentPage,
                initialPagination.pageSize,
                sort.sortColumn,
                sort.sortOrder,
                searchText
            );
        if (searchText === "") {
            resetSelection();
        }
    }, [selectedSortOption]);

    useEffect(() => {
        props.fetchMessages(pagination.currentPage, pagination.pageSize, "");
        resetSelection();
    }, [mailboxUserData]);

    useEffect(() => {
        if (props.clearCheckBoxSelection) {
            resetSelection();
            props.setSelection(false);
            props.messages.length > 0 ? props.setSelectedMessage(props.messages[0]) : null;
        }
    }, [props.clearCheckBoxSelection]);

    useEffect(() => {
        handleResetSearchAndSort(true);
        setCollapseThreads(prevState => !prevState);
    }, [signalRState.refreshSharedFolderRecipient, signalRState.archiveFolderReload]);

    useEffect(() => {
        handleResetSearchAndSort();
    }, [props.reload]);

    const previousPage = useRef(pagination.currentPage);

    useEffect(() => {
        if (
            (previousPage.current !== pagination.currentPage || pagination.currentPage === 1) &&
            props.messages?.length > 0
        ) {
            props.setSelectedMessage(props.messages[0]);
        }
        previousPage.current = pagination.currentPage;
        dispatchSelectedMessageActions({
            type: "removeAll",
            payload: null,
        });
    }, [props.messages]);

    useEffect(() => {
        if (searchText === "") {
            dispatchSelectedMessageActions({
                type: "removeAll",
                payload: null,
            });
        }
        props.messages.length ? props.setSelectedMessage(props.messages[0]) : null;
    }, [searchText]);

    useEffect(() => {
        if (!selectedReceivedMessage?.messages?.length || !props.messages?.length) {
            setIsMoveToFolders(false);
            return;
        }
        const areAllThreadsValid: { allThreadsValid: boolean; isSingleQuickReplyMessagePresent: boolean } =
            areThreadsValid(selectedReceivedMessage.messages, props.messages);

        setIsMoveToFolders(areAllThreadsValid.allThreadsValid && !areAllThreadsValid.isSingleQuickReplyMessagePresent);
    }, [selectedReceivedMessage.messages, props.messages]);

    const prevTotalRecordsRef = useRef(getTotalMessageCount(props.messages));
    useEffect(() => {
        const incomingRecordsCount = getTotalMessageCount(props.messages);
        if (incomingRecordsCount > prevTotalRecordsRef.current) {
            setPagination(initialPagination);
            setSearchText("");
            setSelectedSortOption && setSelectedSortOption(SortAndFilterConstants.NEWEST_FIRST_ORDER);
        } else if (props.totalRecords < prevTotalRecordsRef.current) {
            props.messages.length ? props.setSelectedMessage(props.messages[0]) : null;
        }
        prevTotalRecordsRef.current = incomingRecordsCount;
        setCollapseThreads(prevState => !prevState);
    }, [props.totalRecords, props.messages]);

    useEffect(() => {
        props.messages.length > 0 ? props.setSelectedMessage(props.messages[0]) : null;
    }, []);

    const getData = (value: string) => {
        const sort = getFolderMessageSortOptions(selectedSortOption);
        setPagination(initialPagination);
        props.fetchMessages(pagination.currentPage, pagination.pageSize, sort.sortColumn, sort.sortOrder, value);
    };
    const debounceFn = useCallback(debounce(1000, getData), [selectedSortOption]);

    const onFilter: React.ChangeEventHandler<HTMLInputElement> = e => {
        const value = (e.target as HTMLInputElement).value.toLocaleLowerCase();
        setSearchText(value);
        debounceFn(value);
        if (value == "") {
            resetSelection();
            props.setSelection(false);
            props.messages.length > 0 ? props.setSelectedMessage(props.messages[0]) : null;
        }
    };

    const handleOnPageChange = (pageNo: number, pageSize: number = DEFAULT_PAGE_SIZE) => {
        setPagination({
            pageSize,
            currentPage: pageNo,
            totalRecords: props.totalRecords,
        });
        const sort = getFolderMessageSortOptions(selectedSortOption);
        props.fetchMessages(pageNo, pageSize, sort.sortColumn, sort.sortOrder, searchText);
        props.pageNumberChanged && props.pageNumberChanged(pageNo);
    };

    const handleMessageReload = (isRefresh?: boolean) => {
        //cleares searchtext and default soring options and then reloads messages
        if (isRefresh) {
            setPagination(initialPagination);
            setSelectedSortOption && setSelectedSortOption(SortAndFilterConstants.NEWEST_FIRST_ORDER);
            props.fetchMessages(
                isRefresh ? initialPagination.currentPage : pagination.currentPage,
                pagination.pageSize,
                ""
            );
        }
        setSearchText("");
    };

    const handleResetSearchAndSort = (updateSort?: boolean) => {
        setSearchText("");
        updateSort && setSelectedSortOption && setSelectedSortOption(SortAndFilterConstants.NEWEST_FIRST_ORDER);
        const sort = getFolderMessageSortOptions(selectedSortOption);
        props.fetchMessages(pagination.currentPage, pagination.pageSize, sort.sortColumn, sort.sortOrder);
    };

    const handleOnSelected = (message: IReceivedMessageModel, e: any) => {
        e.stopPropagation();
        if (e.target.checked) {
            dispatchSelectedMessageActions({
                type: "add",
                payload: message,
                messageCount: getTotalMessageCount(props.messages),
            });
        } else {
            dispatchSelectedMessageActions({ type: "remove", payload: message });
        }
    };

    const handleOnAllSelected = (e: any) => {
        if (e.target.checked) {
            dispatchSelectedMessageActions({
                type: "addAll",
                payload: props.messages,
            });
        } else {
            dispatchSelectedMessageActions({
                type: "removeAll",
                payload: null,
            });
        }
    };

    const handleMarkAsNotSpam = (callback?: any) => {
        if (props.markAsNotSpam) {
            props.markAsNotSpam(
                selectedReceivedMessage.messages.map(x => x.receivedMessageId),
                callback
            );
        }
    };

    const handleMoveToInbox = (callback?: any) => {
        if (props.moveToInbox) {
            props.moveToInbox(
                selectedReceivedMessage.messages.map(x => x.receivedMessageId),
                callback
            );
        }
    };

    const handleDeleteMessages = (callback: any) => {
        setSearchText("");
        props.deleteMessages(selectedReceivedMessage.messages, callback);
        dispatchSelectedMessageActions({
            type: "removeAll",
            payload: null,
        });
    };

    const handlePrintDocument = () => {
        props.print(selectedReceivedMessage.messages);
    };

    const handleOnClickMoveToFolder = () => {
        // store selected message as moving messages for moveToFolder dialog
        dispatch({ type: MovingMessageActions.ADD, payload: selectedReceivedMessage.messages });
        //URL format: .../move-to/folderType/isMovingFolder/isDocumentRequest
        const url = `${window.location.origin}/move-to/${props.folderType}/false/false`;
        Office.context.ui.displayDialogAsync(
            url,
            { height: 60, width: 40, displayInIframe: true },
            moveToFolderDialogCallback
        );
    };

    const moveToFolderDialogCallback = (asyncResult: Office.AsyncResult<Office.Dialog>) => {
        const dialog = asyncResult.value;
        dialog.addEventHandler(Office.EventType.DialogMessageReceived, (args: { message: string; origin: string }) => {
            dialog.close();
            const message = args.message;
            if (message === "cancel") {
                resetSelection();
            } else {
                const destinationFolder: IFolderModel = JSON.parse(message);
                handleMoveToFolder(destinationFolder.folderId, result => {
                    if (result) {
                        AppNotifier.Success(ToasterMessages.SUCCESS.MOVE_MESSAGE_TO_FOLDER);
                        props.hideMessageDetailView && props.hideMessageDetailView(true);
                        handleResetSearchAndSort();
                    }
                });
            }
        });
    };

    const handleMoveToFolder = (folderId: number, callback?: any) => {
        setSearchText("");
        props.moveToFolder(selectedReceivedMessage.messages, folderId, callback);
        dispatchSelectedMessageActions({
            type: "removeAll",
            payload: null,
        });
    };

    const handleOnMessageItemClick = (message: IReceivedMessageModel, e: any) => {
        e.stopPropagation();
        props.setSelectedMessage(message);
        props.getMessageDetail(message);
    };

    const resetSelection = () => {
        dispatchSelectedMessageActions({
            type: "removeAll",
            payload: null,
        });
    };

    return (
        <>
            <Row className="search-header-wrapper">
                <Col className="search-input-wrapper-col col-8">
                    <div className="search-input-wrapper">
                        <FormControl
                            placeholder={ArchiveConstants.ShareFolder.PLACEHOLDER_SEARCH_TEXT}
                            value={searchText}
                            onChange={onFilter}
                            className="search-input search-textbox"
                        />
                        <div className="search-icon">
                            <SearchIcon />
                        </div>
                    </div>
                </Col>
                <Col className="filter-icon-container col-2">
                    <div>
                        <FiltersCustomComponent
                            groups={sortingData}
                            selectedSortOption={selectedSortOption}
                            iconType={SortAndFilterIconType.Sort}
                            setSelectedSortOption={setSelectedSortOption}
                        />
                    </div>
                </Col>
                <Col className="refresh-button-container col-2">
                    <div
                        className="refresh-button-wrapper"
                        title={ArchiveConstants.REFRESH_TITLE}
                        onClick={() => {
                            if (props.isFolderRefresh) {
                                props.handleFolderRefreshClick?.();
                            }
                            handleMessageReload(true);
                        }}
                    >
                        <RefreshIcon />
                    </div>
                </Col>
            </Row>
            <div className="flex-row-space-between">
                <MessageToolbar
                    folderType={props.folderType}
                    folderId={props.folderId}
                    isAllSelected={selectedReceivedMessage.isAllSelected}
                    isAnySelected={selectedReceivedMessage.messages.length > 0}
                    selectAll={handleOnAllSelected}
                    deleteMessages={handleDeleteMessages}
                    printDocument={handlePrintDocument}
                    markAsNotSpam={handleMarkAsNotSpam}
                    moveToInbox={handleMoveToInbox}
                    checkFolderExpiry={props.checkFolderExpiry}
                    moveToFolder={handleMoveToFolder}
                    openMoveToFolder={handleOnClickMoveToFolder}
                    reloadMessages={handleMessageReload}
                    hideMessageDetailView={() => props.hideMessageDetailView(true)}
                    resetSelection={resetSelection}
                    showSelectAllCheckbox={props.messages && props.messages.length > 0}
                    resourceLocators={props.resourceLocators}
                    folder={props?.folder}
                    resetSearchAndSort={handleResetSearchAndSort}
                    isMoveToFoldersAllowed={isMoveToFoldersAllowed}
                />

                {props.messages && props.messages.length > 0 && (
                    <DefaultPagination
                        pageSize={pagination.pageSize}
                        currentPage={pagination.currentPage}
                        totalRecords={props.totalRecords}
                        onPageChange={handleOnPageChange}
                    />
                )}
            </div>

            <LoadingOverlay style={{ innerHeight: "400px", outerHeight: "500px" }}>
                <div
                    style={{
                        height: getMessageListWrapperHeight(mailboxUserData, props.folderType === FolderType.ARCHIVE),
                    }}
                    className="message-list"
                >
                    {props.messages && props.messages.length > 0 ? (
                        props.messages.map((item: any, index: number) => {
                            const isChecked =
                                selectedReceivedMessage.messages.includes(item) ||
                                selectedReceivedMessage.isAllSelected;
                            const selectedMessageId =
                                props.selectedMessage.receivedMessageId === 0
                                    ? props.messages[0].receivedMessageId
                                    : props.selectedMessage.receivedMessageId;
                            const selectedMessageOriginFolder =
                                props.selectedMessage.receivedMessageId === 0
                                    ? props.messages[0].folderId
                                    : props.selectedMessage.folderId;
                            return (
                                <MessageListItem
                                    message={item}
                                    key={index}
                                    index={index}
                                    isChecked={isChecked}
                                    selectedMessageId={selectedMessageId}
                                    onSelected={handleOnSelected}
                                    folder={props?.folder}
                                    onClick={(message, e) => handleOnMessageItemClick(message, e)}
                                    selectedReceivedMessage={selectedReceivedMessage}
                                    collapseAllThreads={collapseAllThreads}
                                    selectedMessageOriginFolder={selectedMessageOriginFolder}
                                />
                            );
                        })
                    ) : (
                        <div className="vertical-middle">{MessageListConstants.NO_MESSAGE_TEXT}</div>
                    )}
                </div>
                <Loader loading={props.isLoading} />
            </LoadingOverlay>
        </>
    );
};

export default MessageList;
